/**
 * Private app URLs for AI-composed pages, shared by the MCP tools that
 * return links and the React pages that link between the normal page and
 * its TV-mode takeover. Paths include the `/app` prefix; React Router
 * callers strip it with `appRouterPath` because the router's basename is
 * `/app`.
 */

/** Map (page_kind, instance_id) to the normal private app page. */
export function pageKindToDeeplink(pageKind: string, instanceId: string = "default"): string {
  const inst = encodeURIComponent(instanceId);
  switch (pageKind) {
    case "ai_page": return `/app/reports/${inst}`;
    case "dashboard": return "/app/dashboard";
    case "bills": return "/app/bills";
    case "members": return "/app/members";
    case "bill_detail": return `/app/bills/${inst}`;
    case "member_detail": return `/app/members/${inst}`;
    case "whip_count_bill": return `/app/bills/${inst}/whip-count`;
    case "whip_count_issue": return `/app/issues/${inst}/whip-count`;
    case "issue_detail": return `/app/issues/${inst}`;
    case "contact_detail": return `/app/contacts/${inst}`;
    case "account_detail": return `/app/accounts/${inst}`;
    case "coalition_detail": return `/app/coalitions/${inst}`;
    case "presentation": return `/app/presentations/${inst}`;
    case "scorecards": return "/app/scorecards";
    case "reports": return "/app/reports";
    case "treasury": return "/app/treasury";
    case "designations": return "/app/designations";
    case "map": return "/app/map";
    case "tool_history": return "/app/tool-history";
    default: return `/app/${pageKind}`;
  }
}

/** The bookmarkable full-screen TV-mode URL for a composed page. */
export function tvModePath(pageKind: string, instanceId: string = "default"): string {
  return `/app/tv/${encodeURIComponent(pageKind)}/${encodeURIComponent(instanceId || "default")}`;
}

/** Strip the `/app` basename so the path can be handed to React Router. */
export function appRouterPath(path: string): string {
  return path === "/app" ? "/" : path.startsWith("/app/") ? path.slice(4) : path;
}

/** Append `workspace_id` so the link opens the same workspace. */
export function withWorkspace(path: string, workspaceId: string | null | undefined): string {
  if (!workspaceId) return path;
  return `${path}${path.includes("?") ? "&" : "?"}workspace_id=${encodeURIComponent(workspaceId)}`;
}
